import { IconDot } from "@/assets/icons/geist/IconDot";
import { IconPlaceholder } from "@/assets/icons/geist/IconPlaceholder";
import { EnvironmentDetailsEmptySection } from "@/components/environments/EnvironmentDetailsEmptySection";
import { EnvironmentServiceActions } from "@/components/environments/EnvironmentServiceActions";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { useListServices, type PlainService } from "@/queries/automations-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { canOpen } from "@/routes/environments/phase";
import { ServicePhase } from "gitpod-next-api/gitpod/v1/environment_automation_pb";
import { EnvironmentStatus_ContentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useMemo, type FC, type ReactNode } from "react";

export const EnvironmentServices: FC<{ environmentId: string }> = ({ environmentId }) => {
    const { data: environment } = useEnvironment(environmentId);
    const { data: services, isPending, error } = useListServices(environmentId);
    const ready = (!isPending && services !== undefined) || Boolean(error);

    const count = services ? <span className="font-light">({services.length})</span> : "";

    const disabled = !environment || !canOpen(environment);

    let emptyState: "info" | "skeleton" = "info";
    if (
        environment?.spec?.automationsFile &&
        environment.status?.automationsFile?.phase !== EnvironmentStatus_ContentPhase.FAILED
    ) {
        emptyState = "skeleton";
    }

    return (
        <div>
            <div className="flex items-center justify-between pb-6">
                <Heading3 className="select-none text-lg font-bold text-content-primary">Services {count}</Heading3>
            </div>
            <SkeletonBlock failed={Boolean(error)} ready={ready} className="h-20">
                <Services
                    environmentId={environmentId}
                    services={services || []}
                    disabled={disabled}
                    emptyState={emptyState}
                />
            </SkeletonBlock>
        </div>
    );
};

const Services: FC<{
    services: PlainService[];
    environmentId: string;
    disabled: boolean;
    emptyState: "info" | "skeleton";
}> = ({ services, environmentId, disabled, emptyState }) => {
    if (services.length === 0) {
        if (emptyState === "skeleton") {
            return <SkeletonBlock failed={false} ready={false} className="h-20" />;
        }

        return (
            <EnvironmentDetailsEmptySection
                icon={<IconPlaceholder kind="automations" />}
                title="Add services to your environment"
                text="You can automate services by editing the .gitpod/automations.yaml file."
                docsUrl="https://www.gitpod.io/docs/flex/integrations/ports"
            />
        );
    }

    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {services.map((service) => (
                <ServiceCard key={service.id} service={service} environmentId={environmentId} disabled={disabled} />
            ))}
        </div>
    );
};

const ServiceCard: FC<{ service: PlainService; environmentId: string; disabled: boolean }> = ({
    service,
    environmentId,
    disabled,
}) => {
    return (
        <div className="group flex h-16 items-center gap-2 truncate rounded-xl border border-border-base bg-surface-glass px-4 py-2.5 pl-3 hover:shadow">
            <div className="flex grow flex-row items-start gap-2 truncate">
                <ServiceStatus service={service} />
                <div className="truncat flex w-full min-w-0 flex-col">
                    <Text className="truncate text-base font-bold text-content-primary">{service.metadata?.name}</Text>
                    <Text className="truncate text-sm text-content-secondary">{service.metadata?.description}</Text>
                </div>
            </div>
            <div className="hidden group-hover:flex">
                <EnvironmentServiceActions
                    service={service}
                    environmentId={environmentId}
                    showLogs={true}
                    disabled={disabled}
                />
            </div>
        </div>
    );
};

const ServiceStatus: FC<{ service: PlainService }> = ({ service }) => {
    const size = "lg";
    const icon: ReactNode = useMemo(() => {
        const sameSession = service.status?.session === service.spec?.session;
        if (!sameSession) {
            // Optimistically update state to STARTING
            if (service.spec?.desiredPhase === ServicePhase.RUNNING) {
                return <IconDot size={size} className="text-content-yield" />;
            }
        }
        switch (service.status?.phase) {
            case ServicePhase.UNSPECIFIED:
            case ServicePhase.STOPPED:
            case ServicePhase.DELETED:
                return <IconDot size={size} className="text-content-tertiary" />;
            case ServicePhase.STOPPING:
            case ServicePhase.STARTING:
                return <IconDot size={size} className="text-content-yield" />;
            case ServicePhase.RUNNING:
                if (
                    service.spec?.desiredPhase === ServicePhase.STOPPED ||
                    service.spec?.desiredPhase === ServicePhase.DELETED
                ) {
                    // Optimistically update state to STOPPING.
                    return <IconDot size={size} className="text-content-yield" />;
                }
                return <IconDot size={size} className="text-content-green" />;
            case ServicePhase.FAILED:
                return <IconDot size={size} className="text-content-red" />;
        }
    }, [service.spec?.desiredPhase, service.spec?.session, service.status?.phase, service.status?.session]);
    return <div className="size-6">{icon}</div>;
};
